import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { signOut } from "firebase/auth";
import { useStore } from "zustand";
import { auth } from "./firebase";
import { queryClient, router } from "./main";
import sessionUserStore, { SessionUser } from "./store/auth";
import {
  clearStoredSession,
  onOtherTabSessionChange,
  readStoredSelectedOrganizationId,
  readStoredSelectedSchoolId,
  writeStoredSession,
} from "./store/auth/session";

export type AuthContextType = {
  isAuthenticated: boolean;
  user: SessionUser | null;
  selectedOrganizationId: number | null;
  selectedSchoolId: number | null;
  setSelectedOrganizationId: (id: number) => void;
  setSelectedSchoolId: (id: number) => void;
  login: (user: SessionUser, token: string) => boolean;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  selectedOrganizationId: null,
  selectedSchoolId: null,
  setSelectedOrganizationId: () => {},
  setSelectedSchoolId: () => {},
  login: () => false,
  logout: () => {},
});

export function logOut() {
  queryClient.clear();
  // Admins sign in to Firebase at login; without this the identity outlives the session.
  signOut(auth).catch(() => {});
  clearStoredSession();
  sessionUserStore.getState().setUser(null);
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const user = useStore(sessionUserStore, (state) => state.user);
  const setUser = useStore(sessionUserStore, (state) => state.setUser);
  const [schoolIdOverride, setSelectedSchoolId] = useState<number | null>(
    readStoredSelectedSchoolId(),
  );
  const [organizationIdOverride, setSelectedOrganizationId] = useState<
    number | null
  >(readStoredSelectedOrganizationId());

  // The user object is the source of truth; storage only carries an override
  // (admins switching school/organization). Deriving the fallback from the user
  // is what makes "signed in without a selected school" impossible:
  // sessionUserSchema requires both ids, so a non-null user always has them.
  const selectedSchoolId = schoolIdOverride ?? user?.SchoolID ?? null;
  const selectedOrganizationId =
    organizationIdOverride ?? user?.OrganizationID ?? null;

  const isAuthenticated = !!user;
  const prevIsAuthenticatedRef = useRef(isAuthenticated);

  const login = useCallback((user: SessionUser, token: string) => {
    // Every API request reads the token from storage, so a session whose token
    // did not stick is dead on arrival. Leave the user signed out and let the
    // caller report it, rather than signing in for a string of 401s.
    if (!writeStoredSession(user, token)) {
      clearStoredSession();
      return false;
    }
    setUser(user);
    return true;
  }, []);

  // A reload re-reads the whole session from storage, so the header, the
  // query cache and the requests agree on one user and school again.
  useEffect(() => onOtherTabSessionChange(() => window.location.reload()), []);

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current;
    prevIsAuthenticatedRef.current = isAuthenticated;

    const justLoggedOut = wasAuthenticated && !isAuthenticated;
    if (!justLoggedOut) return;
    if (window.location.pathname === "/login") return;

    const redirect =
      window.location.pathname + window.location.search + window.location.hash;
    router.navigate({ to: "/login", search: { redirect } });
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        selectedOrganizationId,
        selectedSchoolId,
        setSelectedOrganizationId,
        setSelectedSchoolId,
        login,
        logout: logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export type SignedInAuthContextType = {
  isAuthenticated: boolean;
  user: SessionUser;
  selectedOrganizationId: number;
  selectedSchoolId: number;
  setSelectedOrganizationId: (id: number) => void;
  setSelectedSchoolId: (id: number) => void;
  login: (user: SessionUser, token: string) => boolean;
  logout: () => void;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  // The cast is what the route guards buy: (app) and (fullscreen) redirect to
  // /login unless isAuthenticated, and sessionUserSchema requires SchoolID and
  // OrganizationID — so a non-null user always carries the ids this type
  // promises. Re-checking it on every render of ~76 call sites would buy
  // nothing, and throwing from here white-screens the app above every error
  // boundary.
  return context as SignedInAuthContextType;
};
